import { addDays } from "date-fns";
import type { Identifier } from "ra-core";

import { isIsoDay, parseIsoDay, toIsoDay } from "./dates";
import {
  CONVERTIBLE_TYPES,
  isOpportunityStage,
  isOpportunityType,
  OPPORTUNITY_STAGES,
} from "./labels";
import type {
  MarketOpportunity,
  OpportunityStage,
  OpportunityType,
} from "./types";

export type OpportunityColumns = Record<OpportunityStage, MarketOpportunity[]>;

/** Overdue: a pending next step whose date is before today (won/lost never are). */
export const isOverdue = (
  opportunity: Pick<MarketOpportunity, "next_step_date" | "stage">,
  today: string,
): boolean =>
  !!opportunity.next_step_date &&
  opportunity.stage !== "ganada" &&
  opportunity.stage !== "perdida" &&
  opportunity.next_step_date < today;

/**
 * Most urgent first: the earliest next step date (none = last), then the
 * most recently touched.
 */
export const compareOpportunities = (
  left: MarketOpportunity,
  right: MarketOpportunity,
): number => {
  const a = left.next_step_date ?? "9999-12-31";
  const b = right.next_step_date ?? "9999-12-31";
  return (
    a.localeCompare(b) ||
    (right.updated_at ?? "").localeCompare(left.updated_at ?? "") ||
    Number(right.id) - Number(left.id)
  );
};

/** Opportunities per kanban column, each column sorted by urgency. */
export const groupOpportunitiesByStage = (
  opportunities: readonly MarketOpportunity[],
): OpportunityColumns => {
  const columns = Object.fromEntries(
    OPPORTUNITY_STAGES.map((stage) => [stage, [] as MarketOpportunity[]]),
  ) as OpportunityColumns;
  for (const opportunity of opportunities) {
    // Unknown stages (hand-written SQL) land in "nueva" instead of vanishing.
    const stage = isOpportunityStage(opportunity.stage)
      ? opportunity.stage
      : "nueva";
    columns[stage] = [...columns[stage], opportunity];
  }
  for (const stage of OPPORTUNITY_STAGES)
    columns[stage] = [...columns[stage]].sort(compareOpportunities);
  return columns;
};

const isClosed = (opportunity: MarketOpportunity) =>
  opportunity.stage === "ganada" || opportunity.stage === "perdida";

/** List order: open ones by urgency first, then the won / lost ones. */
export const sortForList = (
  opportunities: readonly MarketOpportunity[],
): MarketOpportunity[] =>
  [...opportunities].sort(
    (a, b) =>
      Number(isClosed(a)) - Number(isClosed(b)) || compareOpportunities(a, b),
  );

/** Kanban column dot colours (hex, the shared KanbanColumn takes a colour). */
export const STAGE_COLOR: Record<OpportunityStage, string> = {
  nueva: "#0ea5e9",
  en_contacto: "#f59e0b",
  propuesta: "#8b5cf6",
  ganada: "#10b981",
  perdida: "#94a3b8",
};

/** Sum of the known estimated values. */
export const totalValue = (
  opportunities: readonly MarketOpportunity[],
): number =>
  opportunities.reduce(
    (sum, opportunity) => sum + (Number(opportunity.estimated_value) || 0),
    0,
  );

/** The `useUpdate` payload of a stage move, or null when nothing changes. */
export const stagePatch = (
  opportunity: MarketOpportunity,
  stage: OpportunityStage,
): {
  id: Identifier;
  data: Pick<MarketOpportunity, "stage">;
  previousData: MarketOpportunity;
} | null => {
  if (!isOpportunityStage(stage) || opportunity.stage === stage) return null;
  return { id: opportunity.id, data: { stage }, previousData: opportunity };
};

export const canConvertToDeal = (
  opportunity: Pick<MarketOpportunity, "type" | "deal_id">,
): boolean =>
  CONVERTIBLE_TYPES.includes(opportunity.type) && opportunity.deal_id == null;

/** Days the deal's expected closing date lands after today when unknown. */
const DEFAULT_CLOSING_DAYS = 30;

export interface DealDraft {
  name: string;
  amount: number;
  stage: string;
  expected_closing_date: string;
  description: string;
}

/**
 * Prefilled values of the deal created from an opportunity: same name, the
 * estimated value as amount, the pipeline's first stage, and the next step
 * date (if still ahead) or today + 30 days as expected closing date.
 */
export const dealDraftFromOpportunity = (
  opportunity: MarketOpportunity,
  firstDealStage: string,
  today: string,
): DealDraft => {
  const nextStep = opportunity.next_step_date;
  return {
    name: opportunity.name,
    amount: Number(opportunity.estimated_value) || 0,
    stage: firstDealStage,
    expected_closing_date:
      nextStep && isIsoDay(nextStep) && nextStep >= today
        ? nextStep
        : toIsoDay(addDays(parseIsoDay(today), DEFAULT_CLOSING_DAYS)),
    description: [opportunity.notes, opportunity.next_step]
      .map((text) => text?.trim())
      .filter(Boolean)
      .join("\n\n"),
  };
};

// ---------- form ----------

export interface OpportunityFormValues {
  name: string;
  type: OpportunityType;
  stage: OpportunityStage;
  sector: string;
  contact: string;
  /** As typed: "4.500", "4500,50"… */
  estimated_value: string;
  next_step: string;
  next_step_date: string;
  notes: string;
}

export type OpportunityPayload = Pick<
  MarketOpportunity,
  "name" | "type" | "stage"
> & {
  sector: string | null;
  contact: string | null;
  estimated_value: number | null;
  next_step: string | null;
  next_step_date: string | null;
  notes: string | null;
};

export const opportunityFormDefaults = (
  opportunity?: Partial<MarketOpportunity>,
): OpportunityFormValues => ({
  name: opportunity?.name ?? "",
  type: isOpportunityType(opportunity?.type)
    ? opportunity.type
    : "cliente_potencial",
  stage: isOpportunityStage(opportunity?.stage) ? opportunity.stage : "nueva",
  sector: opportunity?.sector ?? "",
  contact: opportunity?.contact ?? "",
  estimated_value:
    opportunity?.estimated_value != null
      ? String(opportunity.estimated_value)
      : "",
  next_step: opportunity?.next_step ?? "",
  next_step_date: opportunity?.next_step_date ?? "",
  notes: opportunity?.notes ?? "",
});

/**
 * Amount typed the Spanish way ("4.500", "4.500,50", "4500.5") or plain;
 * null when empty, NaN when not a number.
 */
export const parseAmount = (value: string): number | null => {
  const text = value.replace(/[\s€]/g, "");
  if (!text) return null;
  // "4.500" / "1.250.000" (dots as thousands) -> remove them.
  const normalized = /^\d{1,3}(\.\d{3})+(,\d+)?$/.test(text)
    ? text.replace(/\./g, "").replace(",", ".")
    : text.replace(",", ".");
  return /^\d+(\.\d+)?$/.test(normalized) ? Number(normalized) : Number.NaN;
};

const MAX_NAME = 200;

/** Trimmed payload for create/update, or the i18n key of the first error. */
export const normalizeOpportunityForm = (
  values: OpportunityFormValues,
): { ok: true; data: OpportunityPayload } | { ok: false; error: string } => {
  const name = values.name.trim();
  if (!name)
    return { ok: false, error: "market.opportunities.form.name_required" };
  if (name.length > MAX_NAME)
    return { ok: false, error: "market.opportunities.form.name_too_long" };
  const amount = parseAmount(values.estimated_value);
  if (amount !== null && !Number.isFinite(amount))
    return { ok: false, error: "market.opportunities.form.value_invalid" };
  const date = values.next_step_date.trim();
  if (date && !isIsoDay(date))
    return { ok: false, error: "market.opportunities.form.date_invalid" };
  const optional = (value: string) => value.trim() || null;
  return {
    ok: true,
    data: {
      name,
      type: isOpportunityType(values.type) ? values.type : "cliente_potencial",
      stage: isOpportunityStage(values.stage) ? values.stage : "nueva",
      sector: optional(values.sector),
      contact: optional(values.contact),
      estimated_value: amount,
      next_step: optional(values.next_step),
      next_step_date: date || null,
      notes: optional(values.notes),
    },
  };
};
